import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { AiAssistantService } from '@fund-base/services/ai/ai-assistant.service';
import { AiChatMessage, AiChatResponse } from '@fund-base/types/ai/ai-assistant.types';
import { fetchFunds, fetchFundsVisitors } from '@fund-base/store/actions/funds.actions';
import { FiltersSearch, HiddenFundsMode } from '@fund-base/types/funds/funds-search.types';
import { selectUser, selectVisitor } from '@fund-base/store/selectors/auth.selectors';
import { selectFundTypes, selectInterests } from '@fund-base/store/selectors/types.selectors';
import { selectDirection, selectLanguage } from '@fund-base/store/selectors/locale.selectors';
import { FundType, Interest } from '@fund-base/types/types/types.types';
import { User } from '@fund-base/types/auth/auth.types';
import { Direction, Language } from '@fund-base/types/locale/locale.types';
import { resolveAiFundTypes } from '@fund-base/utils/helpers/ai-chat/ai-chat-fund-type.helpers';

export interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PersistedChat {
  displayMessages: DisplayMessage[];
  chatHistory: AiChatMessage[];
}

const CHAT_STORAGE_KEY = 'ai_chat_session';

@Component({
  selector: 'app-ai-chat-widget',
  templateUrl: './ai-chat-widget.component.html',
  styleUrls: ['./ai-chat-widget.component.scss'],
})
export class AiChatWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef<HTMLDivElement>;
  @ViewChild('inputTextarea') inputTextarea: ElementRef<HTMLTextAreaElement>;

  isOpen = false;
  isLoading = false;
  isVisible = false;
  isVisitor = false;
  isRtl = false;
  isHebrew = false;
  inputControl = new FormControl('');

  displayMessages: DisplayMessage[] = [];
  chatHistory: AiChatMessage[] = [];
  pendingSearch: AiChatResponse | null = null;
  pendingSearchInterests: string[] = [];
  pendingSearchType = '';
  userName = '';

  private userId = '';
  private destroy$ = new Subject<void>();

  constructor(
    private aiAssistantService: AiAssistantService,
    private store: Store,
    private elementRef: ElementRef,
    private transloco: TranslocoService,
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  ngOnInit(): void {
    this.store
      .select(selectDirection)
      .pipe(takeUntil(this.destroy$))
      .subscribe((dir: Direction) => {
        this.isRtl = dir === Direction.RTL;
      });

    this.store
      .select(selectLanguage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: Language) => {
        this.isHebrew = lang === Language.Hebrew;
      });

    this.store
      .select(selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User | undefined) => {
        if (user) {
          this.isVisible = true;
          this.isVisitor = false;
          this.userName = user.first_name ?? user.username ?? '';
          this.initSession(String(user.id ?? user.username ?? 'user'));
        }
      });

    this.store
      .select(selectVisitor)
      .pipe(takeUntil(this.destroy$))
      .subscribe((visitor: any) => {
        if (visitor) {
          this.isVisible = true;
          this.isVisitor = true;
          this.userName = visitor.first_name ?? visitor.username ?? '';
          this.initSession(String(visitor.id ?? visitor.username ?? 'visitor'));
        } else if (!this.userName) {
          this.isVisible = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isStartPage(): boolean {
    return this.displayMessages.length === 0;
  }

  get pendingLocationDisplay(): string {
    const loc = this.pendingSearch?.locationOfActivity;
    if (!loc) return '';
    const country = loc.country ? this.transloco.translate(loc.country) : undefined;
    return [loc.city, loc.state, country].filter(Boolean).join(', ');
  }

  get pendingFundTypesDisplay(): string {
    const fundTypes = this.pendingSearch?.fundTypes ?? [];
    if (!fundTypes.length) return this.transloco.translate('None');
    return fundTypes.map(type => this.transloco.translate(type)).join(', ');
  }

  get inputPlaceholder(): string {
    const key = this.isStartPage
      ? 'An after-school STEM program for middle school students in Chicago for low-income communities.'
      : 'Enter your reply here...';
    return this.transloco.translate(key);
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.scrollToBottom();
  }

  clearChat(): void {
    this.displayMessages = [];
    this.chatHistory = [];
    this.pendingSearch = null;
    this.pendingSearchInterests = [];
    this.pendingSearchType = '';
    this.saveSession();
  }

  sendMessage(): void {
    const text = this.inputControl.value?.trim();
    if (!text || this.isLoading) return;

    this.displayMessages.push({ role: 'user', content: text });
    this.inputControl.setValue('');
    this.isLoading = true;
    this.pendingSearch = null;
    this.pendingSearchInterests = [];
    this.scrollToBottom();

    this.aiAssistantService
      .chat({ message: text, history: this.chatHistory, language: this.isHebrew ? 'he' : 'en' }, this.isVisitor)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AiChatResponse) => {
          this.isLoading = false;
          this.chatHistory.push({ role: 'user', content: text });
          // Annotate search turns with the actual filter state so the model can read it
          // back on follow-up commands (e.g. "remove Israel", "add category X").
          // displayMessages still shows only response.reply to the user.
          const historyContent = response.shouldSearch
            ? `${response.reply}\n[Search filters: categoryIds=${JSON.stringify(response.categoryIds ?? [])}, fundTypes=${JSON.stringify(response.fundTypes ?? [])}, country=${response.locationOfActivity?.country ?? null}]`
            : response.reply;
          this.chatHistory.push({ role: 'assistant', content: historyContent });
          this.displayMessages.push({ role: 'assistant', content: response.reply });
          this.saveSession();
          this.scrollToBottom();
          if (response.shouldSearch) {
            this.pendingSearch = response;
            this.pendingSearchType = response.searchType ?? '';
            this.store
              .select(selectInterests)
              .pipe(take(1))
              .subscribe((allInterests: Interest[]) => {
                if (response.categoryIds?.length) {
                  const numericIds = response.categoryIds.map(Number);
                  this.pendingSearchInterests = allInterests
                    .filter(i => numericIds.includes(i.id))
                    .map(i => i.text);
                } else {
                  this.pendingSearchInterests = [];
                }
              });
          }
        },
        error: () => {
          this.isLoading = false;
          this.displayMessages.push({
            role: 'assistant',
            content: this.transloco.translate("Sorry, I'm having trouble connecting. Please try again in a moment."),
          });
          this.scrollToBottom();
        },
      });
  }

  performPendingSearch(): void {
    if (!this.pendingSearch) return;
    const search = this.pendingSearch;
    const criteriaLines: string[] = [];
    if (this.pendingSearchInterests.length) {
      criteriaLines.push(`${this.transloco.translate('Category')}: ${this.pendingSearchInterests.join(', ')}`);
    }
    const translatedFundTypes = (search.fundTypes ?? []).map(type => this.transloco.translate(type));
    criteriaLines.push(
      `${this.transloco.translate('Type')}: ${
        translatedFundTypes.length ? translatedFundTypes.join(', ') : this.transloco.translate('None')
      }`
    );
    if (search.freeText) {
      const label =
        this.pendingSearchType === 'FUND_NAME'
          ? this.transloco.translate('Fund name')
          : this.pendingSearchType === 'FUND_PEOPLE'
            ? this.transloco.translate('Contact person')
            : this.pendingSearchType === 'SUPPORTED_ORGANIZATIONS'
              ? this.transloco.translate('Supported organization')
              : this.transloco.translate('Free Text Search');
      criteriaLines.push(`${label}: ${search.freeText}`);
    }
    if (criteriaLines.length) {
      const title = this.transloco.translate("I'll use these search criteria:");
      this.displayMessages.push({ role: 'assistant', content: `${title}\n${criteriaLines.join('\n')}` });
    }
    this.pendingSearch = null;
    this.pendingSearchInterests = [];
    this.pendingSearchType = '';
    this.saveSession();
    this.triggerSearch(search);
  }

  editMessage(text: string): void {
    this.inputControl.setValue(text);
    setTimeout(() => this.inputTextarea?.nativeElement?.focus(), 0);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private initSession(userId: string): void {
    if (this.userId === userId) return;
    this.userId = userId;
    this.loadSession();
  }

  private storageKey(): string {
    return `${CHAT_STORAGE_KEY}_${this.userId}`;
  }

  private saveSession(): void {
    if (!this.userId) return;
    try {
      const data: PersistedChat = {
        displayMessages: this.displayMessages,
        chatHistory: this.chatHistory,
      };
      localStorage.setItem(this.storageKey(), JSON.stringify(data));
    } catch {
      // storage quota exceeded — silently ignore
    }
  }

  private loadSession(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;
      const data: PersistedChat = JSON.parse(raw);
      this.displayMessages = data.displayMessages ?? [];
      this.chatHistory = data.chatHistory ?? [];
      if (this.isOpen) this.scrollToBottom();
    } catch {
      localStorage.removeItem(this.storageKey());
    }
  }

  private triggerSearch(response: AiChatResponse): void {
    this.isOpen = false;
    document.dispatchEvent(new CustomEvent('atlas:close-advanced-search'));

    this.store
      .select(selectInterests)
      .pipe(take(1))
      .subscribe((allInterests: Interest[]) => {
        this.store
          .select(selectFundTypes)
          .pipe(take(1))
          .subscribe((allFundTypes: FundType[]) => {
            const filtersSearch: FiltersSearch = {
              searchType: 'FUND_NAME',
              type: [],
              locationOfTheFund: null,
              areaOfInterests: [],
              areaOfActivity: [],
              textSearch: '',
              monthsToApply: [],
              webSite: false,
              phone: false,
              newestFunds: false,
              email: false,
              delegations: false,
              limitations: false,
              hiddenFundsMode: HiddenFundsMode.EXCLUDE_HIDDEN,
            };

            if (response.categoryIds?.length) {
              const numericIds = response.categoryIds.map(Number);
              filtersSearch.areaOfInterests = allInterests.filter(i => numericIds.includes(i.id));
            }

            if (response.fundTypes?.length) {
              filtersSearch.type = resolveAiFundTypes(response.fundTypes, allFundTypes);
            }

            // Apply location whenever any field is explicitly set by the AI
            if (response.locationOfActivity) {
              const loc = response.locationOfActivity;
              const country = loc.country?.trim() || '';
              const state = loc.state?.trim() || '';
              const city = loc.city?.trim() || '';

              if (country || state || city) {
                filtersSearch.AreaOfActivityV2 = {
                  country: country
                    ? ({
                        description: country,
                        structured_formatting: { main_text: country, secondary_text: '' },
                        place_id: '',
                        countryCode: this.resolveCountryCode(country),
                        viewport: null,
                      } as any)
                    : null,
                  state: state
                    ? ({
                        description: state,
                        structured_formatting: { main_text: state, secondary_text: '' },
                        place_id: '',
                        viewport: null,
                      } as any)
                    : null,
                  city: city
                    ? ({
                        description: city,
                        structured_formatting: { main_text: city, secondary_text: '' },
                        place_id: '',
                        viewport: null,
                      } as any)
                    : null,
                };
              }
            }

            // Apply fund-name or contact-person text search when AI detected these specific intents.
            // Location is not relevant for name/person searches — strip it regardless of what the AI sent.
            if (response.searchType === 'FUND_NAME' && response.freeText?.trim()) {
              filtersSearch.searchType = 'FUND_NAME';
              filtersSearch.textSearch = response.freeText.trim();
              filtersSearch.AreaOfActivityV2 = undefined;
            } else if (response.searchType === 'FUND_PEOPLE' && response.freeText?.trim()) {
              filtersSearch.searchType = 'FUND_PEOPLE';
              filtersSearch.textSearch = response.freeText.trim();
              filtersSearch.AreaOfActivityV2 = undefined;
            } else if (response.searchType === 'SUPPORTED_ORGANIZATIONS' && response.freeText?.trim()) {
              filtersSearch.searchType = 'SUPPORTED_ORGANIZATIONS';
              filtersSearch.textSearch = response.freeText.trim();
              filtersSearch.AreaOfActivityV2 = undefined;
            }

            // setAdvancedFilters is also dispatched inside the fetchFunds effect, so we
            // don't need to call it here separately. Navigation is also handled by the effect.
            if (this.isVisitor) {
              this.store.dispatch(fetchFundsVisitors({ filtersSearch }));
            } else {
              this.store.dispatch(fetchFunds({ filtersSearch }));
            }
          });
      });
  }

  private resolveCountryCode(country: string): string {
    const normalized = country.trim().toLowerCase();
    if (!normalized) return 'il';
    if (normalized === 'israel' || normalized === 'il' || normalized === 'ישראל') return 'il';
    if (normalized === 'united states' || normalized === 'usa' || normalized === 'u.s.' || normalized === 'us')
      return 'us';
    return normalized;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 20);
  }
}
